import React, { useState, useRef } from 'react';

const MediaGallery = ({ 
  productId, 
  media = [], 
  onMediaChange, 
  onReorder,
  onDelete,
  onUpload,
  isEditing = false 
}) => {
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef(null);

  // Handle file upload
  const handleFileUpload = async (files) => {
    if (!files || files.length === 0) return;

    setUploading(true);
    setUploadProgress(0);

    try {
      const uploadedMedia = [];
      
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        const isVideo = file.type.startsWith('video/');
        const isImage = file.type.startsWith('image/');
        
        if (!isVideo && !isImage) {
          console.warn('Unsupported file type:', file.type);
          continue;
        }

        // Check file size
        const maxSize = isVideo ? 50 * 1024 * 1024 : 10 * 1024 * 1024; // 50MB for videos, 10MB for images
        if (file.size > maxSize) {
          console.warn('File too large:', file.name, file.size);
          continue;
        }

        // Create FormData
        const formData = new FormData();
        formData.append(isVideo ? 'video' : 'image', file);

        // Upload to backend
        const token = localStorage.getItem('adminToken');
        const endpoint = isVideo ? '/api/upload/video' : '/api/upload/image';
        
        const response = await fetch(endpoint, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`
          },
          body: formData
        });

        if (response.ok) {
          const result = await response.json();
          
                     // Create media object
           const newMedia = {
             id: `temp_${Date.now()}_${Math.random().toString(36).substr(2, 9)}_${i}`,
             product_id: productId,
            media_type: isVideo ? 'video' : 'image',
            url: result.file.url,
            [isVideo ? 'video_url' : 'image_url']: result.file.url,
            display_order: media.length + i,
            file_size: file.size,
            mime_type: file.type,
            upload_date: new Date().toISOString(),
            sftpgo_path: `${isVideo ? 'videos' : 'images'}/${file.name}`,
            is_active: true,
            // Video specific fields
            ...(isVideo && { 
              video_title: file.name.replace(/\.[^/.]+$/, ""),
              title: file.name.replace(/\.[^/.]+$/, "")
            })
          };

          uploadedMedia.push(newMedia);
          
          // Update progress
          setUploadProgress(((i + 1) / files.length) * 100);
        } else {
          console.error('Upload failed for:', file.name);
        }
      }

             // Add all uploaded media to the gallery and trigger auto-save
       if (uploadedMedia.length > 0) {
         // Call onUpload to trigger auto-save in ProductManager
         if (onUpload) {
           onUpload(uploadedMedia);
         }
       }

    } catch (error) {
      console.error('Upload error:', error);
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  // Handle file input change
  const handleFileInputChange = (e) => {
    const files = Array.from(e.target.files);
    handleFileUpload(files);
    e.target.value = ''; // Reset input
  };

  // Handle drag and drop file upload
  const handleDragOver = (e) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setDragOver(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    
    const files = Array.from(e.dataTransfer.files);
    handleFileUpload(files);
  };

  // Handle media deletion
  const handleDelete = async (mediaId, mediaType) => {
    if (!onDelete) return;

    try {
      // Find the media item
      const mediaItem = media.find(item => item.id === mediaId);
      if (!mediaItem) return;

      // Extract filename from URL
      let filename;
      const url = mediaItem.url || mediaItem.image_url || mediaItem.video_url;
      
      if (url.includes('/api/upload/')) {
        filename = url.split('/').pop();
      } else if (url.includes('sftpgo.caprover.distracker.com.mx/public/webstoreuser/')) {
        filename = url.split('/').pop();
      } else {
        console.warn('Could not extract filename from URL:', url);
        return;
      }

      // Delete from SFTPGo
      const token = localStorage.getItem('adminToken');
      const directory = mediaType === 'video' ? 'videos' : 'images';
      
      const response = await fetch(`/api/upload/files/${directory}/${encodeURIComponent(filename)}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        console.log('✅ File deleted from SFTPGo:', filename);
      } else {
        console.warn('⚠️ Failed to delete file from SFTPGo:', filename);
      }

      // Remove from local state
      const updatedMedia = media.filter(item => item.id !== mediaId);
      onMediaChange(updatedMedia);

    } catch (error) {
      console.error('❌ Error deleting media:', error);
    }
  };

  // Handle media reordering
  const handleReorder = (mediaId, newOrder) => {
    if (!onReorder) return;

    const updatedMedia = media.map(item => 
      item.id === mediaId 
        ? { ...item, display_order: newOrder }
        : item
    );

    // Sort by display order
    updatedMedia.sort((a, b) => a.display_order - b.display_order);
    
    // Update display order to be sequential
    const finalMedia = updatedMedia.map((item, index) => ({
      ...item,
      display_order: index
    }));

    onReorder(finalMedia);
  };

  // Move media item up or down
  const moveMedia = (mediaId, direction) => {
    const currentIndex = media.findIndex(item => item.id === mediaId);
    if (currentIndex === -1) return;

    const newIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
    if (newIndex < 0 || newIndex >= media.length) return;

    const updatedMedia = [...media];
    const [movedItem] = updatedMedia.splice(currentIndex, 1);
    updatedMedia.splice(newIndex, 0, movedItem);

    // Update display order
    const finalMedia = updatedMedia.map((item, index) => ({
      ...item,
      display_order: index
    }));

    onReorder(finalMedia);
  };

  // Render media item
  const renderMediaItem = (item, index) => {
    const isVideo = item.media_type === 'video' || item.video_url;
    const mediaUrl = item.url || item.image_url || item.video_url;
    
    return (
      <div key={item.id} className="media-item">
        <div className="media-preview">
          {isVideo ? (
            <video 
              src={mediaUrl} 
              controls 
              width="100%" 
              height="120"
              onError={(e) => {
                console.error('Video failed to load:', mediaUrl);
                e.target.style.display = 'none';
              }}
            />
          ) : (
            <img 
              src={mediaUrl} 
              alt={`Media ${index + 1}`}
              onError={(e) => {
                console.error('Image failed to load:', mediaUrl);
                e.target.style.display = 'none';
              }}
            />
          )}
          
          {isEditing && (
            <>
              <button
                type="button"
                onClick={() => handleDelete(item.id, isVideo ? 'video' : 'image')}
                className="delete-media-btn"
                title="Delete"
              >
                ×
              </button>
              
              <div className="media-order">
                <input
                  type="number"
                  value={item.display_order}
                  onChange={(e) => {
                    const newOrder = parseInt(e.target.value) || 0;
                    handleReorder(item.id, newOrder);
                  }}
                  min="0"
                  className="order-input"
                />
              </div>

              <div className="media-controls">
                <button
                  type="button"
                  onClick={() => moveMedia(item.id, 'up')}
                  disabled={index === 0}
                  className="move-btn move-up"
                  title="Move Up"
                >
                  ↑
                </button>
                <button
                  type="button"
                  onClick={() => moveMedia(item.id, 'down')}
                  disabled={index === media.length - 1}
                  className="move-btn move-down"
                  title="Move Down"
                >
                  ↓
                </button>
              </div>
            </>
          )}
        </div>
        
        {isVideo && (
          <div className="media-info">
            <p className="media-title">
              {item.video_title || item.title || 'Untitled Video'}
            </p>
            <p className="media-size">
              {(item.file_size / (1024 * 1024)).toFixed(2)} MB
            </p>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="media-gallery">
      <div className="gallery-header">
        <h3>Media Gallery</h3>
        {isEditing && (
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            className="btn btn-primary btn-sm"
            disabled={uploading}
          >
            {uploading ? 'Uploading...' : 'Add Media'}
          </button>
        )}
      </div>

      {/* Upload Progress */}
      {uploading && (
        <div className="upload-progress">
          <div className="progress-bar">
            <div 
              className="progress-fill" 
              style={{ width: `${uploadProgress}%` }}
            ></div>
          </div>
          <p className="progress-text">Uploading... {Math.round(uploadProgress)}%</p>
        </div>
      )}

      {/* Drag & Drop Zone */}
      {isEditing && (
        <div
          className={`drag-drop-zone ${dragOver ? 'drag-over' : ''}`}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          <div className="drag-drop-content">
            <p>📁 Drag & drop files here or</p>
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className="btn btn-secondary btn-sm"
            >
              Browse Files
            </button>
            <p className="drag-drop-info">
              Images: JPG, PNG, GIF, WebP (max 10MB)<br/>
              Videos: MP4, AVI, MOV, WMV, FLV, WebM (max 50MB)
            </p>
          </div>
        </div>
      )}

      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        multiple
        accept="image/*,video/*"
        onChange={handleFileInputChange}
        style={{ display: 'none' }}
      />

      {/* Media Grid */}
      {media.length > 0 ? (
        <div className="media-grid">
          {media.map((item, index) => renderMediaItem(item, index))}
        </div>
      ) : (
        <div className="empty-gallery">
          <p>No media uploaded yet.</p>
          {isEditing && (
            <p>Upload images and videos to create your product gallery.</p>
          )}
        </div>
      )}
    </div>
  );
};

export default MediaGallery;
